/**
 * An ordered walk through the map, and which residues differ along it (`V131`, `V132`, P27, P28).
 *
 * ## What this module is for
 *
 * The reader picks several points in order — start to end — and the question is *what changed*.
 * That question is answered per residue position, and it is answered here rather than in the panel
 * or in the card, because four surfaces ask it about the same list: the panel's own entries, the
 * pinned card on the map, a decoded point's card on the map, and the detail pane. A diff computed
 * in any one of them would be a second implementation the moment the second surface wanted it.
 *
 * **Pure, and no DOM.** `marks.ts` is the precedent and the reason is the same: the tests must be
 * able to import the law without importing the renderer (`oop_contracts §8`, enforced by
 * `boundary.test.ts`).
 *
 * ## Three kinds of point, and why the entry is a union rather than one widened record (`V132`, `V137`)
 *
 * A walk may mix corpus rows, the model's own decoded points, and sequences the reader typed,
 * because the comparison being made — *how does this sequence differ from that one* — does not
 * become a different question when one of the two was generated or written by hand. But the three
 * are not interchangeable:
 *
 * | | a stored point | a generated point | an encoded point |
 * |---|---|---|---|
 * | identity | `datum.row`, an atlas row (`V51`) | `mark.id`, a record in `decoded.ts` | `mark.id`, a record in `encoded.ts` |
 * | who wrote the sequence | the corpus | the model | **the reader** |
 * | who chose the position | the corpus | **the reader**, by clicking | the model, by encoding |
 * | the sequence | read out of the tile (`V13`) | the server's stored `DecodeResponse` | the reader's own input, stored verbatim |
 * | how it is styled | `sequence stored`, teal (P9.5) | `sequence generated`, amber | `sequence typed`, violet (`V137`) |
 * | its card on the map | `pinnedPointCard`, pinned by row | `decodedMark`'s card, by id | `encodedMark`'s card, by id |
 * | it can stop existing | no — the row is in the atlas | **yes** — right-click deletes it, and `CAP_PER_VIEW` evicts it | **yes** — right-click deletes it, and a rebuild makes it stale |
 *
 * **Rejected: one record with `row: -1` for a generated point.** Every generated entry would then
 * share one identity, so a walk could hold at most one of them; and the entry would be a
 * `PointDatum`, which is what `pinnedPointCard` renders in **stored** style — the one distinction
 * P9.5 does not allow this app to blur. The union costs a discriminant and a `keyOf`.
 *
 * `keyOf` is what replaced `datum.row` as the list's identity. Nothing here indexes by row any
 * more, and `rowKey`/`markKey`/`encodedKey` are the only three places a key is spelled.
 *
 * ## The rule, and the two that were rejected
 *
 * `Baseline` offers exactly two, both of which the reader asked for by name. Take four steps where
 * position 3 goes `Ala → Gly → Gly → Ala`:
 *
 * | | step 2 | step 3 | step 4 |
 * |---|---|---|---|
 * | `'start'` — differs from step 1 | 3 | 2, 3 | 2 |
 * | `'previous'` — differs from the step before | 3 | 2 | 3 |
 * | *the union of everything that ever differed* — **rejected** | 3 | 2, 3 | **2, 3** |
 *
 * The rejected rule highlights position 3 on step 4, where the residue **is `Ala`, exactly as it
 * is at the start**: the card would mark a residue as differing from a sequence it is identical
 * to. On a map whose claim is that nothing on screen misdescribes what it sits on (`V14`), that is
 * the one of the three that cannot be shipped.
 *
 * ## Positions, not an alignment
 *
 * Position *i* differs when the codes differ, or when one sequence has no position *i* at all.
 *
 * **The cost, stated:** a 15-mer against the same peptide plus one residue at the front reads as
 * differing everywhere, where an aligner would report one insertion. Not fixed here — an alignment
 * needs a scoring model over 1,203 monomers (`V90`) and the gap penalty would then decide which
 * residues are highlighted, which is a modelling decision hiding inside a UI feature. What this
 * module does instead is report `lengthDelta`, so the caller can say the length differs in words
 * rather than leaving the reader to infer it from a wall of highlight.
 *
 * A generated sequence makes this cost easier to meet rather than harder: the decoder emits its own
 * length, so a mixed walk hits the length case more often than a walk of corpus rows does, and the
 * words are what carry it.
 *
 * The `<bos>` / `<eos>` / `<pad>` codes are compared like any other code: a position where one
 * sequence has ended and the other has not is a real difference. They are still never *hoverable*
 * — `NOT_A_RESIDUE` in `panels/residues.ts` decides that, and it is a separate question.
 */

import type { DecodedMark } from './decoded';
import type { EncodedMark } from './encoded';
import type { PointDatum } from './renderer';

/** Which sequence each entry is compared against (`V131`). The reader's A and C. */
export type Baseline = 'start' | 'previous';

/**
 * A corpus row in the walk: **the tile datum, captured whole at click time**, and the projection it
 * was captured under.
 *
 * **The datum is held rather than three fields copied out of it, and that is what makes the panel
 * work at all.** It came out of the tile (`V13`), so the list, the diff, the highlight, the detail
 * pane and the re-placement of a closed card all need nothing from the renderer, no tile to be
 * loaded, and no request — including after a projection change that has unloaded the tile it was
 * read from. Copying `row`, `sequence` and `xy` out of it instead would be three fields that can
 * disagree with the datum beside them.
 *
 * **`datum.row`, never `datum.ix`, everywhere it is used as an identity** (`V51`): `ix` is
 * deepscatter's rendering rank and on a subsampled view it names a different peptide.
 *
 * `datum.x` / `datum.y` are *this projection's* coordinates for that row, which is why
 * `projection` travels with the entry. A card is placed from those two numbers, and a projection
 * change makes them name a position the new embedding never assigned to that row — the `V115`
 * failure that put eight landmarks 11,000 px off a 1,200 px canvas. So an entry captured under
 * another projection is listed and **not placeable**, rather than placed somewhere wrong.
 */
export interface StoredPoint {
  readonly kind: 'stored';
  readonly datum: PointDatum;
  readonly projection: string;
}

/**
 * A decoded point in the walk: **the whole `DecodedMark` record** (`V132`).
 *
 * The record already carries everything the walk needs — the id, the coordinates, and the server's
 * verbatim `DecodeResponse` — and it already carries the view it belongs to, so `projection` is
 * read off it rather than passed in beside it and given a chance to disagree.
 *
 * **`mark.card` on this copy is not read anywhere.** `decoded.ts` re-parses storage on every call,
 * so the object held here and the object in `main.ts`'s `pane.marks` are different objects that
 * both describe one record; whether the card is open is asked of the pane, never of the entry. The
 * sequence and the coordinates cannot drift, because nothing in the app ever rewrites them.
 *
 * **The record can be destroyed while the entry survives**, which is the one thing a stored entry
 * cannot do — see `EntryStatus['gone']`.
 */
export interface GeneratedPoint {
  readonly kind: 'generated';
  readonly mark: DecodedMark;
  readonly projection: string;
}

/**
 * A sequence the reader typed, in the walk: **the whole `EncodedMark` record** (`V137`, P29).
 *
 * Held whole for `GeneratedPoint`'s reason — the record already carries the sequence, the
 * coordinates and the view they belong to, so `projection` is read off it rather than passed
 * beside it and given a chance to disagree.
 *
 * **The sequence here is the reader's input, never a reconstruction.** Nothing in this app fetches
 * a reconstruction, so there is only one sequence to hold; but the choice is recorded because it
 * is the one that matters when that changes. The walk is a claim about where a set of peptides
 * sits, and the peptide is the one the reader wrote — diffing the model's reconstruction of it
 * would highlight residues the reader never typed, on a card labelled with the sequence they did,
 * which is exactly the error the rejected rule above was rejected for.
 *
 * **The record can be destroyed while the entry survives**, as a decoded one can, and by one more
 * route: a rebuilt atlas leaves it in storage and off the map. See `EntryStatus['gone']`.
 */
export interface EncodedPoint {
  readonly kind: 'encoded';
  readonly mark: EncodedMark;
  readonly projection: string;
}

export type TrajectoryPoint = StoredPoint | GeneratedPoint | EncodedPoint;

export const storedPoint = (datum: PointDatum, projection: string): StoredPoint => ({
  kind: 'stored',
  datum,
  projection,
});

/** The projection comes off the record, so the two cannot disagree. */
export const generatedPoint = (mark: DecodedMark): GeneratedPoint => ({
  kind: 'generated',
  mark,
  projection: mark.projection,
});

/** As `generatedPoint`, and for the same reason. */
export const encodedPoint = (mark: EncodedMark): EncodedPoint => ({
  kind: 'encoded',
  mark,
  projection: mark.projection,
});

/**
 * The list's identity, **and the reason it is a string rather than a number** (`V132`).
 *
 * An atlas row and a decoded record are numbered by two authorities that know nothing about each
 * other: row 7 of the atlas and the seventh decode of the session are different points that would
 * collide under any shared numeric key. The prefix is what keeps them apart, and it is spelled in
 * exactly two functions so that a call site cannot invent a third spelling.
 */
export const rowKey = (row: number): string => `row:${row}`;
export const markKey = (id: string): string => `mark:${id}`;
/**
 * `enc:`, and it must not be `mark:`. Both stores mint their ids from `Date.now()` and a counter,
 * so a decoded record and an encoded one really can carry the same string — two different points
 * that would collide in the walk under a shared prefix.
 */
export const encodedKey = (id: string): string => `enc:${id}`;

export const keyOf = (point: TrajectoryPoint): string => {
  switch (point.kind) {
    case 'stored':
      return rowKey(point.datum.row);
    case 'generated':
      return markKey(point.mark.id);
    case 'encoded':
      return encodedKey(point.mark.id);
  }
};

/**
 * The sequence to be diffed, whichever kind of point holds it.
 *
 * For an encoded point this is the reader's **input** — see `EncodedPoint` for why that is not an
 * arbitrary pick between two candidates.
 */
export const sequenceOf = (point: TrajectoryPoint): string => {
  switch (point.kind) {
    case 'stored':
      return point.datum.sequence;
    case 'generated':
      return point.mark.result.sequence;
    case 'encoded':
      return point.mark.sequence;
  }
};

/**
 * Which of the three sequence styles this point's text takes (P9.5, `V137`).
 *
 * Here rather than in each of the four surfaces that render a walk entry, because "how is a
 * sequence of this provenance coloured" is exactly the distinction the stylesheet is not allowed
 * to blur, and four spellings of it is four chances to blur it.
 */
export const styleOf = (point: TrajectoryPoint): 'stored' | 'generated' | 'typed' =>
  point.kind === 'stored' ? 'stored' : point.kind === 'generated' ? 'generated' : 'typed';

/**
 * How a decoded point's coordinates are written, in the one place that decides it.
 *
 * Here rather than in `panels/detail.ts`, where it used to live privately, because the panel list
 * and the map card now print the same phrase and two spellings of it would drift.
 */
export const atLabel = (xy: readonly [number, number]): string =>
  `(${xy[0].toFixed(4)}, ${xy[1].toFixed(4)})`;

/**
 * What an entry *is*, in the words the panel and the cards both use.
 *
 * **It names the provenance rather than only the identity**, which is P9.5 in text: `row 918` is a
 * peptide that was really in the corpus, and `generated at (…)` is one the model wrote. A panel
 * entry that printed a bare id for the second would make a mixed walk unreadable.
 *
 * **Short, without the "not a corpus member" clause the map card carries.** The panel is one
 * `--panel-width` column and the coloured sequence sits directly under this line, which is P9.5's
 * own channel; the full sentence belongs on a card floating over the map, where nothing else is
 * beside it. `panels/detail.ts` composes that one from `atLabel`.
 *
 * **An encoded point prefers the reader's own name for it** (`V137`). A label is what someone
 * pasting eighty peptides gave them, and eighty entries all reading `typed at (…)` is a list
 * nobody can use; the coordinate is the fallback for a composer entry that was never named.
 */
export const originOf = (point: TrajectoryPoint): string => {
  switch (point.kind) {
    case 'stored':
      return `row ${point.datum.row}`;
    case 'generated':
      return `generated at ${atLabel(point.mark.xy)}`;
    case 'encoded':
      return point.mark.label !== '' ? point.mark.label : `typed at ${atLabel(point.mark.xy)}`;
  }
};

/**
 * Whether this entry's card can be put back on the map, and why not when it cannot (`V132`).
 *
 * One value per entry, computed by `main.ts` — which is the only place that knows what is pinned,
 * what is drawn and what projection is on screen — and consumed by the panel, which turns it into
 * a control and a phrase. Four values rather than two booleans, because the two reasons an entry
 * cannot be placed need two different sentences:
 *
 * | | what it means | the `place` control |
 * |---|---|---|
 * | `placed` | its card is on the map right now | absent |
 * | `placeable` | it can be put back, and this is the ordinary state of a closed card | offered |
 * | `off-projection` | the entry's coordinates belong to another projection (`V115`) | disabled |
 * | `gone` | **generated only** — the record is no longer on the map, so there is nothing to place | disabled |
 *
 * `gone` has no stored counterpart: an atlas row does not stop existing while the atlas is loaded,
 * and an atlas change empties the walk.
 *
 * **An encoded point reaches `gone` by a route a decoded one cannot** (`V139`, P29): its record
 * survives a rebuilt atlas and is deliberately not drawn, because the coordinate belongs to an
 * embedding that no longer exists. `encoded.ts` calls that *stale*, and the composer offers to
 * re-encode it; here it is `gone`, for the same reason eviction is — the entry names a point that
 * is not on this map, and no gesture of the reader's put it in that state.
 *
 * **What reaches `gone` narrowed at `V133`.** A *deleted* decoded point is now taken out of the walk
 * entirely — deletion is a deliberate gesture and an entry naming a destroyed point is not a step of
 * anything — so what is left is the record that disappears **without a gesture**: `CAP_PER_VIEW`
 * evicting the oldest of 200 when the reader decodes elsewhere. That case must not renumber a walk,
 * which is why the status still exists rather than being folded into the removal.
 */
export type EntryStatus = 'placed' | 'placeable' | 'off-projection' | 'gone';

/** What one comparison found. `total` is the longer of the two sequences, in positions. */
export interface DiffSummary {
  /** Per position, `true` where the two differ. Length `total`. */
  readonly positions: readonly boolean[];
  readonly differing: number;
  readonly total: number;
  /** This sequence's length minus the baseline's, in positions. */
  readonly lengthDelta: number;
}

/**
 * One entry's place in the walk, as the four surfaces need it.
 *
 * `index` and `baselineIndex` are **0-based positions in the list**, and every label built from
 * them adds one. `baselineIndex` is `null` for an entry that has nothing to be compared against —
 * step 1 under `'start'`, and step 1 under `'previous'` — which is also exactly when `summary` is
 * `null`. Two fields rather than one because the label names the baseline's step number, and under
 * `'previous'` that is not step 1.
 */
export interface Step {
  readonly index: number;
  readonly total: number;
  readonly baselineIndex: number | null;
  readonly summary: DiffSummary | null;
}

/** Split a sequence cell into residue codes. The one place the separator is named. */
export const codesOf = (sequence: string): string[] => (sequence === '' ? [] : sequence.split('-'));

/**
 * Per-position differences between two sequences, over the longer of the two.
 *
 * A position present in one and absent in the other counts as differing: that is what makes a
 * length change visible rather than silently truncating the comparison to the shorter sequence.
 */
export function diffPositions(baseline: string, other: string): boolean[] {
  const a = codesOf(baseline);
  const b = codesOf(other);
  const total = Math.max(a.length, b.length);
  const positions: boolean[] = [];
  for (let i = 0; i < total; i++) positions.push(a[i] !== b[i]);
  return positions;
}

export function diffSummary(baseline: string, other: string): DiffSummary {
  const positions = diffPositions(baseline, other);
  return {
    positions,
    differing: positions.filter(Boolean).length,
    total: positions.length,
    lengthDelta: codesOf(other).length - codesOf(baseline).length,
  };
}

/** The 0-based entry `at` is compared against, or `null` when there is nothing to compare to. */
export function baselineIndexOf(at: number, baseline: Baseline): number | null {
  if (at <= 0) return null;
  return baseline === 'start' ? 0 : at - 1;
}

/**
 * The whole list's steps, aligned with the list.
 *
 * Computed for every entry on every render rather than cached on the entry. **A stored step number
 * would be a second source of truth for the array's own order**, and the first reorder that forgot
 * to rewrite it would leave two entries claiming to be step 3.
 *
 * **Both kinds are compared as sequences and nothing else** (`V132`). The diff is arithmetic over
 * two hyphen-separated strings; where each string came from decides how it is *styled*, never what
 * it is compared against.
 */
export function steps(list: readonly TrajectoryPoint[], baseline: Baseline): Step[] {
  return list.map((point, index) => {
    const from = baselineIndexOf(index, baseline);
    const against = from === null ? undefined : list[from];
    return {
      index,
      total: list.length,
      baselineIndex: from,
      summary: against === undefined ? null : diffSummary(sequenceOf(against), sequenceOf(point)),
    };
  });
}

/**
 * The compact form: what the panel's list prints beside a step.
 *
 * **It names the baseline's step number rather than saying "differ"**, because the baseline is
 * selectable (`V131`). Under `'previous'` the number changes per entry, and a label that left it
 * out would make a screenshot of the panel unreadable without knowing which mode it was taken in.
 */
export function diffLabel(step: Step): string {
  const summary = step.summary;
  if (summary === null || step.baselineIndex === null) return 'start';
  return `${summary.differing} of ${summary.total} differ from step ${step.baselineIndex + 1}`;
}

/**
 * The full form, as `·`-separated parts: what a card on the map and the detail pane print.
 *
 * The length clause is the visible half of the "positions, not an alignment" cost stated in the
 * module docstring: a sequence two residues longer than its baseline says so in words, instead of
 * leaving the reader to infer it from a tail of highlighted positions.
 */
export function stepLabel(step: Step): string[] {
  const parts = [`step ${step.index + 1} of ${step.total}`, diffLabel(step)];
  const summary = step.summary;
  if (summary !== null && step.baselineIndex !== null && summary.lengthDelta !== 0) {
    const n = Math.abs(summary.lengthDelta);
    const way = summary.lengthDelta > 0 ? 'longer' : 'shorter';
    parts.push(`${n} residue${n === 1 ? '' : 's'} ${way} than step ${step.baselineIndex + 1}`);
  }
  return parts;
}

/** Where `key` sits in the list, or `-1`. */
export const indexOfKey = (list: readonly TrajectoryPoint[], key: string): number =>
  list.findIndex((point) => keyOf(point) === key);

/**
 * Append a point, **ignoring one that is already in the list**.
 *
 * A walk that visited the same point twice would give one sequence two step numbers and two
 * different diffs, and its card is keyed by that same identity so only one of them could ever be
 * drawn. The click that produced this still updates the detail pane, which is the existing
 * behaviour of a repeat click in inspect mode.
 *
 * Returns the same array when nothing was added, so a caller can tell.
 */
export function append(
  list: readonly TrajectoryPoint[],
  point: TrajectoryPoint,
): readonly TrajectoryPoint[] {
  return indexOfKey(list, keyOf(point)) >= 0 ? list : [...list, point];
}

/**
 * Drop the entry at `at`.
 *
 * **This is not the same gesture as closing that point's card** — the reader's decision, 2026-08-28.
 * Removal takes the point out of the walk, so it loses its step number and its highlight; the card
 * stays on the map as an ordinary card of its own kind. `main.ts` is what keeps that true, by
 * rebuilding every card after any change here.
 */
export function removeAt(
  list: readonly TrajectoryPoint[],
  at: number,
): readonly TrajectoryPoint[] {
  if (at < 0 || at >= list.length) return list;
  return [...list.slice(0, at), ...list.slice(at + 1)];
}

/**
 * Drop every entry whose key is in `keys`, **by identity rather than by position** (`V133`).
 *
 * What a deletion calls, where `removeAt` is what the panel's `×` calls. The two are different
 * gestures on purpose: the panel removes *that row of the list*, and a deletion removes *whatever
 * entry named the thing that is gone*, which may sit anywhere in any pane's walk.
 *
 * Returns the same array when nothing matched, so a caller can skip a render.
 */
export function withoutKeys(
  list: readonly TrajectoryPoint[],
  keys: ReadonlySet<string>,
): readonly TrajectoryPoint[] {
  if (keys.size === 0) return list;
  const next = list.filter((point) => !keys.has(keyOf(point)));
  return next.length === list.length ? list : next;
}

/**
 * Swap the entry at `at` with its neighbour in direction `by`.
 *
 * A move off either end returns the same array rather than throwing: the panel disables those
 * controls, and a list operation that trusts its caller's arithmetic is a list operation that
 * breaks the first time the panel is re-rendered mid-click.
 */
export function moveBy(
  list: readonly TrajectoryPoint[],
  at: number,
  by: -1 | 1,
): readonly TrajectoryPoint[] {
  const to = at + by;
  if (at < 0 || at >= list.length || to < 0 || to >= list.length) return list;
  const next = [...list];
  const moved = next[at] as TrajectoryPoint;
  next[at] = next[to] as TrajectoryPoint;
  next[to] = moved;
  return next;
}
